import { useContext, useMemo, useState } from 'react';

import { Form, Formik } from 'formik';
import { useDebouncedCallback } from 'use-debounce';
import * as Yup from 'yup';
import { z } from 'zod';

import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { ArticleParentStrings, Defaults } from '../../../constants';
import { useGetCategoriesByPage } from '../../../lib/category/hooks';
import { useGetSubCategoriesByPage } from '../../../lib/category/hooks/useGetSubCategoriesByPage';
import { useAddAdditionalParentCategory } from '../../../lib/post/hooks';
import { Category } from '../../../types/Category';

type AddParentCategoryButtonProps = {
  postId: string;
  existingParentCategoryIds?: string[];
  primaryCategoryId?: string;
  subcategory?: boolean;
};

function AddParentCategoryButton({
  postId,
  existingParentCategoryIds = [],
  primaryCategoryId,
  subcategory = false,
}: AddParentCategoryButtonProps) {
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    debouncedSearchCategoryFn.cancel();
    setSearchCategory('');
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  const [searchCategory, setSearchCategory] = useState('');
  const debouncedSearchCategoryFn = useDebouncedCallback((value: string) => {
    setSearchCategory(value);
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const { mutate: addAdditionalParentCategory, isLoading } = useAddAdditionalParentCategory();

  const {
    data: categories,
    isFetching: loadingCategories,
    isError: categoriesFailed,
  } = useGetCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
    enabled: open && !subcategory,
  });
  const {
    data: subCategories,
    isFetching: loadingSubCategories,
    isError: subCategoriesFailed,
  } = useGetSubCategoriesByPage({
    page: 0,
    sort: '',
    search: searchCategory,
    enabled: open && subcategory,
  });
  const loadingOptions = subcategory ? loadingSubCategories : loadingCategories;
  const optionsFailed = subcategory ? subCategoriesFailed : categoriesFailed;
  const availableCategories = subcategory ? subCategories?.data : categories;

  const options = useMemo(
    () =>
      (availableCategories ?? []).filter(
        (category) => category.id !== primaryCategoryId && !existingParentCategoryIds.includes(category.id),
      ),
    [availableCategories, existingParentCategoryIds, primaryCategoryId],
  );

  const handleSubmit = (values: SelectCategoryFormType) => {
    addAdditionalParentCategory(
      { postId, parentCategoryId: values.categoryId },
      {
        onSuccess: () => {
          handleClose();
          setAlertSnackbar({
            message: subcategory ? ArticleParentStrings.SUBCATEGORY_ADDED : ArticleParentStrings.CATEGORY_ADDED,
            severity: 'success',
            vertical: 'top',
          });
        },
        onError: (error) => {
          const parsedError = z.object({ message: z.string() }).safeParse(error);
          setAlertSnackbar({
            message: parsedError.success ? parsedError.data.message : ArticleParentStrings.ADD_FAILED,
            severity: 'error',
            vertical: 'top',
          });
        },
      },
    );
  };

  return (
    <>
      <Button variant="contained" onClick={handleOpen} type="button" disabled={!postId}>
        {ArticleParentStrings.ADD}
      </Button>

      <Dialog
        open={open}
        onClose={isLoading ? undefined : handleClose}
        aria-labelledby={`add-parent-${subcategory ? 'subcategory' : 'category'}-title`}
      >
        <Formik
          initialValues={{
            categoryId: '',
          }}
          validationSchema={Yup.object({
            categoryId: Yup.string().required(
              subcategory ? ArticleParentStrings.SUBCATEGORY_REQUIRED : ArticleParentStrings.CATEGORY_REQUIRED,
            ),
          })}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, setFieldValue, handleChange, handleBlur }) => (
            <Form>
              <DialogTitle id={`add-parent-${subcategory ? 'subcategory' : 'category'}-title`}>
                {subcategory ? ArticleParentStrings.SELECT_SUBCATEGORY : ArticleParentStrings.SELECT_CATEGORY}
              </DialogTitle>
              <DialogContent>
                <Autocomplete<Pick<Category, 'id' | 'title'> & { parent?: { title: string } }>
                  sx={{ minWidth: { sm: 320 }, pt: 2 }}
                  loading={loadingOptions}
                  options={options}
                  getOptionLabel={(option) => (subcategory && option.parent ? `${option.parent.title} / ${option.title}` : option.title)}
                  value={options.find((c) => c.id === values.categoryId) || null}
                  onChange={(_, value) => {
                    setFieldValue('categoryId', value ? value.id : '');
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={optionsFailed || (touched.categoryId && !!errors.categoryId)}
                      type="text"
                      name="categoryId"
                      label={subcategory ? ArticleParentStrings.SUBCATEGORY : ArticleParentStrings.CATEGORY}
                      onChange={(e) => {
                        handleChange(e);
                        debouncedSearchCategoryFn(e.target.value);
                      }}
                      onBlur={handleBlur}
                      value={values.categoryId}
                      helperText={optionsFailed ? ArticleParentStrings.LOAD_FAILED : touched.categoryId && errors.categoryId}
                    />
                  )}
                />
              </DialogContent>
              <StyledDialogActions>
                <Button disabled={isLoading} onClick={handleClose} type="button">
                  {ArticleParentStrings.CANCEL}
                </Button>
                <SubmitButton
                  loading={isLoading}
                  disabled={isLoading || loadingOptions || optionsFailed || !options.some((option) => option.id === values.categoryId)}
                  autoFocus
                  variant="contained"
                  type="submit"
                >
                  {ArticleParentStrings.SUBMIT}
                </SubmitButton>
              </StyledDialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
}

export default AddParentCategoryButton;

type SelectCategoryFormType = {
  categoryId: string;
};

const StyledDialogActions = styled(DialogActions)({
  padding: '1rem',
});
